import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import OverlayReelSet from "../OverlayReelSet/OverlayReelSet";
import WinText from "../../../Components/Animations/WinText/WinText";
import { WIN_TEXT_CONFIG } from "../../../Components/Animations/WinText/WinTextController";
import ReelFront from "../../../Components/Animations/ReelFront/ReelFront";
import ReelFrameDecoration from "../../../Components/Animations/ReelFrameDecoration/ReelFrameDecoration";
import ReelBack from "../../../Components/Animations/ReelBack/ReelBack";
import ReelBackDecoration from "../../../Components/Animations/ReelBackDecoration/ReelBackDecoration";
import WinLinesContainer from "../../Shared/WinLinesReelSet/WinLinesContainer";
import WinBoxContainer from "../../Shared/WinBoxReelSet/WinBoxContainer";
import DimReelSet from "../../Shared/DimReelSet/DimReelSet";
import DividerReelset from "../../Shared/DividerReelset/DividerReelset";
import ReelSet from "../ReelSet/ReelSet";
import InfoOverlay from "../../Shared/InfoOverlay/InfoOverlay";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
// import { useSelector } from "react-redux";
// import { selectFreeSpinsActive } from "../../../State/Slices/freeSpinsSlice";
// import FreeSpinGameContainer from "../../FreeSpin/FreeSpinGameContainer/FreeSpinGameContainer";
import TensionReelSet from "../TensionReelSet/TensionReelSet";
import HoldAndWinReelSet from "../../HoldAndWin/HoldAndWinReelSet";
import HoldAndWinOverlayReelSet from "../../HoldAndWin/HoldAndWinOverlayReelSet";
import HoldAndWinTensionOverlaySet from "../../HoldAndWin/HoldAndWinTensionOverlaySet";
import { useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectFreeRoundsActive,
  selectFreeRoundsEnded,
  selectFreeRoundsCompletionPending,
  selectErrorRecoveryTransitionActive,
  selectErrorRecoveryScreenCovered,
} from "../../../State/selectors";
import { createTensionShakeAnimation } from "../../../utils/animations";
import anime from "animejs";
import HoldAndWinCounter from "../../../Components/Animations/HoldAndWinCounter/HoldAndWinCounter";
import HoldAndWinProgressBar from "../../../Components/Animations/HoldAndWinProgressBar/HoldAndWinProgressBar";
import HoldAndWinCollectorDisplay from "../../../Components/Animations/HoldAndWinCollectorDisplay/HoldAndWinCollectorDisplay";
import HoldAndWinCoinCollect from "../../../Components/Animations/HoldAndWinCoinCollect/HoldAndWinCoinCollect";
import FreeRoundsCounter from "../../../Components/UserInterface/secondary/Displays/FreeRoundsCounter";
import WinDisplay from "../../../Components/UserInterface/primary/Displays/WinDisplay";
import { Settings } from "../../../settings";
import FreeSpinSceneTransition from "../../../Components/Animations/FreeSpin/FreeSpinSceneTransition/FreeSpinSceneTransition";

// Game container Y offset config (multiplied by symbolHeight, subtracted from center)
// Positive values push the reels UP from center
// Exported so BackgroundAnimationController can follow same offsets
export const GAME_CONTAINER_OFFSET_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.6,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.325,
  },
  [PixiSettings.DEVICE.DESKTOP]: 0.2,
};

const GameContainer = React.forwardRef((props, ref) => {
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    dividerDimensions,
    reelSpacing,
    orientation,
    device,
  } = useDimensions();

  const {
    dimReelSetRef,
    gameContainerRef,
    backgroundAnimationControllerRef,
    winLinesContainerRef,
    winBoxContainerRef,
    holdAndWinCollectorDisplayRef,
    holdAndWinCoinCollectRef,
    holdAndWinProgressBarRef,
    reelFrameDecorationRef,
    globalLineWinTextRef,
    lineWinTextRef,
  } = useReelRefs();
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsEnded = useSelector(selectFreeRoundsEnded);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  const errorRecoveryScreenCovered = useSelector(
    selectErrorRecoveryScreenCovered,
  );
  // const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null); // Inner container for shakeable elements
  const tensionShakeAnimationRef = useRef(null);

  // Memoize container position calculations
  const containerY = useMemo(() => {
    const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[device];
    const offsetMultiplier =
      typeof deviceConfig === "number"
        ? deviceConfig
        : (deviceConfig?.[orientation] ?? 0.325);
    return dimensions.height / 2 - symbolHeight * offsetMultiplier;
  }, [dimensions.height, symbolHeight, device, orientation]);

  const containerX = useMemo(() => dimensions.width / 2, [dimensions.width]);

  // Memoize Free Rounds display condition
  const shouldShowFreeRoundsCounter = useMemo(
    () =>
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
      !freeRoundsEnded &&
      (freeRoundsActive || freeRoundsCompletionPending) &&
      !holdAndWinActive &&
      (!errorRecoveryTransitionActive || errorRecoveryScreenCovered),
    [
      freeRoundsEnded,
      freeRoundsActive,
      freeRoundsCompletionPending,
      holdAndWinActive,
      errorRecoveryTransitionActive,
      errorRecoveryScreenCovered,
    ],
  );

  // Play tension shake animation (only if tension is enabled)
  const playTensionShake = useCallback(() => {
    // Skip if tension is disabled
    if (!FEATURES_CONFIG.TENSION.ENABLED) {
      return;
    }

    if (
      reelContainerRef.current &&
      symbolHeight &&
      !tensionShakeAnimationRef.current
    ) {
      // Only create shake animation if REELSET_ANIMATION is enabled
      if (FEATURES_CONFIG.TENSION.REELSET_ANIMATION) {
        tensionShakeAnimationRef.current = createTensionShakeAnimation(
          reelContainerRef.current,
          symbolHeight,
        );
      }

      // Play base_event intro animation (only if BACKGROUND_ANIMATION is enabled)
      if (FEATURES_CONFIG.TENSION.BACKGROUND_ANIMATION) {
        backgroundAnimationControllerRef.current?.playBaseEventIn?.();
      }
    }
  }, [symbolHeight, backgroundAnimationControllerRef]);

  // Stop tension shake animation smoothly
  // forceStop parameter allows collection to stop the shake even during Hold and Win
  const stopTensionShake = useCallback(
    (forceStop = false) => {
      // Skip if tension is disabled
      if (!FEATURES_CONFIG.TENSION.ENABLED) {
        return;
      }

      // CRITICAL: Don't stop shake during Hold and Win UNLESS forceStop is true
      // This allows the shake to persist across spins but still be stopped when collection starts
      if (holdAndWinActive && !forceStop) {
        return;
      }

      if (tensionShakeAnimationRef.current) {
        tensionShakeAnimationRef.current.pause();
        tensionShakeAnimationRef.current = null;

        // Smoothly revert to original position (reel container, not main container)
        if (reelContainerRef.current) {
          anime({
            targets: reelContainerRef.current,
            x: 0,
            y: 0,
            rotate: 0,
            duration: 600,
            easing: "easeOutQuad",
          });
        }
      }

      // Play base_event exit animation (only if BACKGROUND_ANIMATION is enabled)
      if (FEATURES_CONFIG.TENSION.BACKGROUND_ANIMATION) {
        if (backgroundAnimationControllerRef.current?.playBaseEventOut) {
          backgroundAnimationControllerRef.current.playBaseEventOut();
        }
      }
    },
    [holdAndWinActive, backgroundAnimationControllerRef],
  );

  // Expose play/stop methods via ref
  useImperativeHandle(
    gameContainerRef,
    () => ({
      playTensionShake,
      stopTensionShake,
    }),
    [playTensionShake, stopTensionShake],
  );

  // Update position when dimensions change and stop animation if active
  useEffect(() => {
    if (containerRef.current) {
      // Update container position when Hold & Win state changes
      containerRef.current.y = containerY;

      // Skip tension animation restart if tension is disabled
      if (!FEATURES_CONFIG.TENSION.ENABLED) {
        return;
      }

      // If animation is running, stop it and restart with new position
      // BUT: Don't restart during Hold and Win - let it continue until collection
      if (tensionShakeAnimationRef.current && !holdAndWinActive) {
        stopTensionShake();
        // Restart animation with new position (only for base game)
        playTensionShake();
      }
    }
  }, [
    dimensions.width,
    dimensions.height,
    symbolHeight,
    holdAndWinActive,
    containerY,
    playTensionShake,
    stopTensionShake,
  ]);

  return (
    <Container
      ref={containerRef}
      name="game_container"
      x={containerX}
      y={containerY}
      sortableChildren
      eventMode="passive"
      zIndex={5}
    >
      {/* Inner container for shakeable elements (reels, symbols, etc.) */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={0}
        y={0}
        sortableChildren
        eventMode="passive"
      >
        {/* Event animations (event_in → event_idle) behind reel frame, after free spin transition */}
        {FEATURES_CONFIG.FREE_SPINS.ENABLED && (
          <ReelBackDecoration
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dimensions={dimensions}
            dividerDimensions={dividerDimensions}
          />
        )}

        <ReelBack
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        <ReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <HoldAndWinReelSet />
        )}

        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <HoldAndWinOverlayReelSet
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <HoldAndWinTensionOverlaySet />
        )}

        <DimReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          ref={dimReelSetRef}
        />

        <ReelFront
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dimensions={dimensions}
          dividerDimensions={dividerDimensions}
        />

        {/* Reel Frame Decorations (claws) - Only during Hold and Win */}
        {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
          <ReelFrameDecoration
            ref={reelFrameDecorationRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            dimensions={dimensions}
            dividerDimensions={dividerDimensions}
            orientation={orientation}
          />
        )}

        {FEATURES_CONFIG.DIVIDERS.ENABLED && (
          <DividerReelset
            symbolHeight={symbolHeight}
            dividerDimensions={dividerDimensions}
            reelSpacing={reelSpacing}
          />
        )}

        {FEATURES_CONFIG.WIN_BOX.ENABLED && (
          <WinBoxContainer
            ref={winBoxContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}

        <OverlayReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        {/* Unified WinText components */}
        {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
          <Container name="win_text_overlay" zIndex={12}>
            {/* Global win text - larger font, shown during "show all wins" phase */}
            <WinText
              ref={globalLineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />

            {/* Line win text - smaller font, shown during individual line cycling */}
            <WinText
              ref={lineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
              hideOnBigWin={false}
            />
          </Container>
        )}

        {FEATURES_CONFIG.WIN_LINES.ENABLED && (
          <WinLinesContainer
            ref={winLinesContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        )}

        {FEATURES_CONFIG.TENSION.ENABLED && !holdAndWinActive && (
          <TensionReelSet />
        )}

        <InfoOverlay />
      </Container>

      {/* WinDisplay under reelset — only visible on mobile portrait, primary theme only */}
      {Settings.APP.UI_THEME === "primary" && <WinDisplay variant="reelset" />}

      {/* Static elements (not affected by shake) */}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinProgressBar ref={holdAndWinProgressBarRef} />
      )}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinCounter />
      )}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinCollectorDisplay ref={holdAndWinCollectorDisplayRef} />
      )}
      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive && (
        <HoldAndWinCoinCollect ref={holdAndWinCoinCollectRef} />
      )}

      {/* Free Rounds Counter - positioned at bottom center of reels */}
      {shouldShowFreeRoundsCounter && <FreeRoundsCounter />}

      {/* {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinGameContainer />} */}

      {/* Free Spin Scene Transition — inside game_container, below UI */}
      {FEATURES_CONFIG.FREE_SPINS.ENABLED && <FreeSpinSceneTransition />}
    </Container>
  );
});

GameContainer.displayName = "GameContainer";

export default GameContainer;
